import { Link } from 'react-router-dom';
import { Download, PackageCheck, Lock, Wrench, Info } from 'lucide-react';
import { formatHardwareId, type PortalUpdateAppliance, type PortalUpdatesResponse } from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi } from '@/lib/cloudApi';
import { useAsync } from '@/lib/useAsync';
import { Card, EmptyState, LoadError, Loading, PortalHeader, formatDate, timeAgo } from './components';

const BASE = (import.meta.env.VITE_CLOUD_URL || '').replace(/\/+$/, '');
const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

function ApplianceUpdates({ a, latest }: { a: PortalUpdateAppliance; latest: string | null }) {
  const upToDate = !!a.installedVersion && a.installedVersion === latest;
  const newestEligible = a.releases.find((r) => r.eligible);
  const blocked = a.releases.filter((r) => !r.eligible && r.reason === 'not_covered');

  return (
    <section className="glass-panel overflow-hidden">
      <div className="p-5 flex flex-col md:flex-row md:items-start gap-4 border-b border-glass-border">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-bold">{a.applianceName ?? `Appliance …${a.hardwareId.slice(-4)}`}</h2>
            {upToDate ? (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-status-running/10 text-status-running">Up to date</span>
            ) : a.installedVersion && newestEligible?.newerThanInstalled ? (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">Update available</span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
            <span className="font-mono">{formatHardwareId(a.hardwareId)}</span>
            <span>
              Installed: {a.installedVersion ? <b className="text-text-primary">{a.installedVersion}</b> : 'unknown'}
              {a.installedVersionReportedAt && ` (reported ${timeAgo(a.installedVersionReportedAt)})`}
            </span>
          </div>
        </div>
        <div className="text-sm md:text-right">
          <div className="flex md:justify-end items-center gap-1.5">
            <Wrench size={14} className={a.maintenanceActive ? 'text-status-running' : 'text-text-muted'} />
            {a.maintenanceActive ? <span>Maintenance active{a.maintenanceRenewsAt ? ` · renews ${formatDate(a.maintenanceRenewsAt)}` : ''}</span> : <span className="text-text-secondary">No maintenance</span>}
          </div>
          <div className="text-xs text-text-muted mt-0.5">{a.coveredUntil ? `Covers releases up to ${formatDate(a.coveredUntil)}` : 'No license on this appliance'}</div>
        </div>
      </div>

      {a.releases.length === 0 ? (
        <p className="p-5 text-sm text-text-muted">No releases published yet.</p>
      ) : (
        <ul className="divide-y divide-glass-border">
          {a.releases.map((r) => (
            <li key={r.id} className="p-5 flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">Version {r.version}</span>
                  <span className="text-xs text-text-muted">
                    Released {formatDate(r.releasedAt)} · {mb(r.bytes)}
                    {r.minFromVersion && ` · requires ${r.minFromVersion} or later`}
                  </span>
                  {a.installedVersion === r.version && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-ink-muted text-text-secondary">Installed</span>}
                </div>
                {r.notes && <p className="mt-1.5 text-sm text-text-secondary whitespace-pre-wrap">{r.notes}</p>}
                {!r.eligible && (
                  <p className="mt-1.5 text-xs text-status-idle flex items-start gap-1.5">
                    <Lock size={12} className="mt-0.5 shrink-0" /> {r.message}
                  </p>
                )}
              </div>
              {r.eligible ? (
                <Button href={`${BASE}/api/portal/updates/${encodeURIComponent(r.id)}/download?hardwareId=${a.hardwareId}`} size="sm" variant={r === newestEligible ? 'primary' : 'secondary'}>
                  <Download size={13} /> Download for this appliance
                </Button>
              ) : r.reason === 'not_covered' && a.licenseId ? (
                <Button to="/portal/licenses" size="sm" variant="secondary">
                  Renew maintenance
                </Button>
              ) : r.reason === 'no_license' ? (
                <Button to={`/portal/licenses/new?hwid=${a.hardwareId}`} size="sm" variant="secondary">
                  Get a license
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {blocked.length > 0 && (
        <p className="px-5 py-3 text-xs text-text-secondary border-t border-glass-border bg-ink-muted/40">
          {blocked.length} newer release{blocked.length === 1 ? ' needs' : 's need'} maintenance. Your license keeps working — renewing maintenance unlocks every release published since it lapsed.
        </p>
      )}
    </section>
  );
}

export function UpdatesPage() {
  usePageTitle('Software updates');
  const { data, error, loading, reload } = useAsync(() => cloudApi.get<PortalUpdatesResponse>('/api/portal/updates'));

  return (
    <>
      <PortalHeader
        title="Software updates"
        description="Download updates for your appliances. You can install any version released while that appliance’s maintenance was active — even after it lapses."
      />
      {loading && !data ? (
        <Loading />
      ) : error || !data ? (
        <LoadError message={error ?? 'Could not load updates.'} onRetry={reload} />
      ) : data.appliances.length === 0 ? (
        <Card>
          <EmptyState icon={PackageCheck} title="No licensed appliances" body="Updates are offered per appliance once it has a license on your account." action={<Button to="/portal/licenses/new">Buy a license</Button>} />
        </Card>
      ) : (
        <div className="space-y-5">
          {data.appliances.map((a) => (
            <ApplianceUpdates key={a.hardwareId} a={a} latest={data.latestVersion} />
          ))}
        </div>
      )}
      <Card title="Installing an update" className="mt-8">
        <ol className="list-decimal ml-5 space-y-1.5 text-sm text-text-secondary">
          <li>Download the update for the specific appliance — each file carries an authorization for that appliance’s hardware ID and won’t install on another.</li>
          <li>
            On the appliance, open <b>Appliance → System → Software update</b> and upload the file. It is checked before anything changes.
          </li>
          <li>Apply it. The appliance restarts, checks itself, and rolls back automatically if the new version doesn’t come up healthy.</li>
        </ol>
        <p className="mt-4 flex items-start gap-2 text-xs text-text-muted">
          <Info size={13} className="mt-0.5 shrink-0" />
          Works for appliances with no internet connection — download here, install on site. <Link to="/portal/licenses" className="text-brand-primary font-semibold">Manage maintenance</Link>
        </p>
      </Card>
    </>
  );
}
